'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/app/store';
import { selectCurrentLanguage, selectIsLoadingLanguage, selectLanguageError, selectAvailableLanguagesList } from '@/app/store/slices/languageSlice';
import { updateUserLanguagePreference } from '@/app/store/thunks/languageThunks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLanguage, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { useLanguageTranslation } from '../hooks/useTranslation';
import { useAuth } from '@/app/hooks/useAuth';

interface LanguageSelectorProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  size = 'medium',
  className = ''
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useLanguageTranslation();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const isLoading = useSelector(selectIsLoadingLanguage);
  const error = useSelector(selectLanguageError);
  const availableLanguages = useSelector(selectAvailableLanguagesList);
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use new auth system directly
  const { user: currentUser, isAuthenticated } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleLanguageChange = useCallback(async (languageCode: string) => {
    if (languageCode === currentLanguage) {
      setIsOpen(false);
      return;
    }

    // Import language utilities
    const { updateHtmlAttributes, saveGuestLanguagePreference } = await import('../utils/languageUtils');
    
    // Store previous language for potential rollback
    const previousLanguage = currentLanguage;
    
    // Update HTML attributes immediately for instant UI feedback
    updateHtmlAttributes(languageCode as any);

    // Clear DB translation cache so the new locale is fetched fresh
    const { clearCache: clearTranslationCache } = await import('@/app/store/slices/translationSlice');
    dispatch(clearTranslationCache());

    // If user is authenticated, update in database
    if (isAuthenticated && currentUser?.id) {
      try {
        // Dispatch thunk to update language preference (partitionKey defaults to PARTITION_KEY)
        await dispatch(updateUserLanguagePreference(parseInt(currentUser.id), languageCode as any));
        
        setIsOpen(false);
      } catch (err) {
        console.error('Failed to update language:', err);
        
        // On critical failure, revert to previous language
        updateHtmlAttributes(previousLanguage);
        
        // Error is already handled in the thunk and stored in Redux state
        // Keep dropdown open so user can see error and retry
      }
    } else {
      // Guest user - save to local storage and update Redux
      saveGuestLanguagePreference(languageCode as any);
      
      // Import and dispatch setLanguage action
      const { setLanguage } = await import('@/app/store/slices/languageSlice');
      dispatch(setLanguage(languageCode as any));
      
      setIsOpen(false);
    }
  }, [currentLanguage, isAuthenticated, currentUser?.id, dispatch]);

  const handleRetry = useCallback(() => {
    // Clear error and allow user to try again
    const { clearLanguageError } = require('@/app/store/slices/languageSlice');
    dispatch(clearLanguageError());
  }, [dispatch]);

  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);

  const getDropdownPosition = (): React.CSSProperties => {
    if (!buttonRef.current) {
      return { top: '100%', right: 0 };
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 280;
    const viewportWidth = window.innerWidth;
    const spaceOnRight = viewportWidth - rect.right;
    
    return {
      position: 'fixed',
      top: rect.bottom + 5, // small gap below the top bar for visual separation
      right: spaceOnRight < dropdownWidth ? 16 : viewportWidth - rect.right,
      width: dropdownWidth,
      zIndex: 999999,
    };
  };

  return (
    <div className={`language-selector ${className}`}>
      <Button
        ref={buttonRef}
        variant="ghost"
        size={size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'default'}
        onClick={handleToggleDropdown}
        className="h-8 w-8 p-0 transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
        aria-label="Select language"
        title="Select language"
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        ) : (
          <FontAwesomeIcon
            icon={faLanguage}
            className="transition-colors text-white"
          />
        )}
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          style={getDropdownPosition()}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('selectLanguage')}
            </h3>
            <button
              onClick={handleCloseDropdown}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <p className="text-sm text-red-600 dark:text-red-400 flex-1">{error}</p>
                <button
                  onClick={handleRetry}
                  className="ml-2 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Language List */}
          <div className="p-2">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isLoading}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                  currentLanguage === lang.code
                    ? 'bg-primary/10 text-primary dark:bg-primary/20'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium">{lang.nativeName}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{lang.name}</span>
                </div>
                {currentLanguage === lang.code && (
                  <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
